import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@sunfish/lib/utils';

const radioCardVariants = cva(
	'flex gap-3 p-3 rounded-md border border-transparent cursor-pointer transition-colors text-left',
	{
		variants: {
			orientation: {
				horizontal: 'flex-row items-start',
				vertical: 'flex-col items-stretch h-full'
			},
			variant: {
				default: 'hover:bg-muted/50 data-[state=checked]:border-primary data-[state=checked]:bg-primary/5',
				brand: 'hover:bg-muted/50 data-[state=checked]:border-brand data-[state=checked]:bg-brand/5',
				success:
					'hover:bg-muted/50 data-[state=checked]:border-green-500 data-[state=checked]:bg-green-50 dark:data-[state=checked]:bg-green-950 dark:data-[state=checked]:border-green-600',
				destructive:
					'hover:bg-muted/50 data-[state=checked]:border-red-500 data-[state=checked]:bg-red-50 dark:data-[state=checked]:bg-red-950 dark:data-[state=checked]:border-red-600'
			}
		},
		defaultVariants: {
			variant: 'default',
			orientation: 'horizontal'
		}
	}
);

const radioIndicatorVariants = cva(
	'aspect-square h-4 w-4 rounded-full border shadow-sm flex items-center justify-center mt-0.5 shrink-0 transition-colors',
	{
		variants: {
			variant: {
				default: 'border-primary',
				brand: 'border-muted-foreground group-data-[state=checked]:border-brand',
				success:
					'border-muted-foreground group-data-[state=checked]:border-green-500 dark:group-data-[state=checked]:border-green-400',
				destructive:
					'border-muted-foreground group-data-[state=checked]:border-red-500 dark:group-data-[state=checked]:border-red-400'
			}
		},
		defaultVariants: {
			variant: 'default'
		}
	}
);

const radioDotVariants = cva('h-2 w-2 rounded-full', {
	variants: {
		variant: {
			default: 'bg-primary',
			brand: 'bg-brand',
			success: 'bg-green-500 dark:bg-green-400',
			destructive: 'bg-red-500 dark:bg-red-400'
		}
	},
	defaultVariants: {
		variant: 'default'
	}
});

const radioIconVariants = cva('text-muted-foreground transition-colors', {
	variants: {
		variant: {
			default: 'group-data-[state=checked]:text-primary',
			brand: 'group-data-[state=checked]:text-brand',
			success: 'group-data-[state=checked]:text-green-600 dark:group-data-[state=checked]:text-green-400',
			destructive: 'group-data-[state=checked]:text-red-600 dark:group-data-[state=checked]:text-red-400'
		}
	},
	defaultVariants: {
		variant: 'default'
	}
});

type RadioCardProps = React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> &
	VariantProps<typeof radioCardVariants> & {
		title: string;
		description?: string;
		icon?: React.ReactNode;
		children?: React.ReactNode;
	};

const RadioCard = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Item>, RadioCardProps>(
	({ className, variant, orientation, title, description, icon, children, ...props }, ref) => {
		const isVertical = orientation === 'vertical';
		const indicator = (
			<div className={cn(radioIndicatorVariants({ variant }), isVertical && 'mt-0')}>
				<RadioGroupPrimitive.Indicator className={cn(radioDotVariants({ variant }))} />
			</div>
		);

		return (
			<RadioGroupPrimitive.Item
				ref={ref}
				className={cn('group', radioCardVariants({ variant, orientation }), className)}
				{...props}
			>
				{isVertical ? (
					// Icon and indicator share a top row so the tiles line up on their
					// titles regardless of how long each description wraps.
					<div className="flex items-center justify-between">
						{icon && <span className={cn(radioIconVariants({ variant }))}>{icon}</span>}
						{indicator}
					</div>
				) : (
					indicator
				)}
				<div className={cn('min-w-0', !isVertical && 'flex-1')}>
					<div className={cn('font-medium text-sm', isVertical && 'mt-3')}>{title}</div>
					{description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
					{children}
				</div>
			</RadioGroupPrimitive.Item>
		);
	}
);
RadioCard.displayName = 'RadioCard';

export { RadioCard, radioCardVariants };
